/**
 * ReportDesigner (ADR-0061 S4 + S4b hardening) — the custom absolute canvas,
 * shaped on the DevExpress web designer:
 *
 * - LEFT: the control TOOLBOX (icon strip — click or drag onto a band).
 * - CENTER: mm RULER + band lanes with a ROTATED band strip on their left
 *   edge; controls drag (multi), resize from 8 handles, snap to the 1mm grid
 *   AND to sibling edges (smart guides); marquee-select on empty lane space.
 * - RIGHT: vertical tab strip → Proprietà (element dropdown + accordion
 *   sections) | Campi (searchable Type tree, draggable — the field-drop
 *   gesture).
 * - TOOLBAR: cut/copy/paste/delete · undo/redo (transactional history — a
 *   drag is ONE entry) · align/distribute (multi-select) · zoom · the
 *   DESIGN | ANTEPRIMA mode toggle (preview = the real paginated HTML,
 *   full-canvas).
 * - KEYBOARD: Canc, arrows nudge 1mm (Shift=5), Esc, Ctrl+Z/Y/C/V/D.
 *
 * `detail_report` bands nest their lane set recursively with the bound
 * collection's scope. Geometry goes through `lib/reportGeometry` (WYSIWYG
 * parity — the renderer emits the SAME mm as literal CSS units). Deferred-test
 * FE tier: the pure model (`lib/reportAst`, `lib/reportGeometry`) is
 * unit-tested; the canvas is owner-verified visually.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  ArrowLeft,
  Boxes,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  ClipboardPaste,
  Copy,
  Database,
  Download,
  FileType2,
  GripVertical,
  Hash,
  Image as ImageIcon,
  List,
  Minus,
  Pilcrow,
  Plus,
  Printer,
  QrCode,
  Redo2,
  Scissors,
  Search,
  SeparatorHorizontal,
  Settings2,
  Square,
  StretchHorizontal,
  StretchVertical,
  Table as TableIcon,
  Trash2,
  Type as TypeIcon,
  Undo2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { reportTypesApi, type ReportTypeNode } from "@/lib/api-reports";
import {
  AGG_BANDS,
  AGG_OPS,
  BAND_DISPLAY_ORDER,
  BAND_KINDS,
  addControlAt,
  alignControls,
  bandAt,
  bandVisualHeightMm,
  cloneControls,
  flatCollections,
  flattenScope,
  distributeControls,
  laneWidthMm,
  newBand,
  newControl,
  normalizeLayout,
  removeBandAt,
  scopeNodesFor,
  sortBands,
  tableFromCollection,
  updateBandAt,
  updateControlAt,
  type AlignMode,
  type BandKind,
  type BandPath,
  type BarcodeControl,
  type FieldControl,
  type ReportBand,
  type ReportControl,
  type ReportLayout,
  type TableControl,
} from "@/lib/reportAst";
import { clampBox, mmToPx, pxToMm, rulerTicks, snapMm, snapToSiblings } from "@/lib/reportGeometry";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";

// ---------------------------------------------------------------------------
// Selection + drag payloads
// ---------------------------------------------------------------------------

type Selection =
  | { kind: "page" }
  | { kind: "band"; path: BandPath }
  | { kind: "control"; path: BandPath; indices: number[] };

const FIELD_MIME = "application/x-tray-report-field";
const CTL_MIME = "application/x-tray-report-ctl";

const BAND_LABELS: Record<BandKind, string> = {
  report_header: "Intestazione report",
  page_header: "Intestazione pagina",
  group_header: "Intestazione gruppo",
  detail: "Dettaglio",
  detail_report: "Sotto-report",
  group_footer: "Piè gruppo",
  page_footer: "Piè pagina",
  report_footer: "Piè report",
};

/** One-line hint per band kind (the add-band popover). */
const BAND_HINTS: Record<BandKind, string> = {
  report_header: "Una volta, all'inizio del report",
  page_header: "In alto su ogni pagina",
  group_header: "All'inizio di ogni gruppo",
  detail: "Ripetuta per ogni riga di dati",
  detail_report: "Sotto-report su una collection annidata",
  group_footer: "Alla fine di ogni gruppo (totali)",
  page_footer: "In basso su ogni pagina",
  report_footer: "Una volta, alla fine del report (totali)",
};

const BAND_TINT: Record<BandKind, string> = {
  report_header: "bg-sky-200/70 dark:bg-sky-900/50",
  page_header: "bg-teal-200/70 dark:bg-teal-900/50",
  group_header: "bg-amber-200/70 dark:bg-amber-900/50",
  detail: "bg-indigo-200/70 dark:bg-indigo-900/50",
  detail_report: "bg-violet-200/70 dark:bg-violet-900/50",
  group_footer: "bg-amber-100/70 dark:bg-amber-950/50",
  page_footer: "bg-teal-100/70 dark:bg-teal-950/50",
  report_footer: "bg-sky-100/70 dark:bg-sky-950/50",
};

const TOP_BAND_KINDS: BandKind[] = [...BAND_KINDS].sort(
  (a, b) => BAND_DISPLAY_ORDER[a] - BAND_DISPLAY_ORDER[b],
);
const NESTED_BAND_KINDS: BandKind[] = ["report_header", "detail", "detail_report", "report_footer"];
const STRIP_W = 22; // px — the rotated band strip on the lane's left edge
const VRULER_W = 16; // px — the per-lane vertical mm ruler left of the strip

function samePath(a: BandPath, b: BandPath): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

type Box = { x: number; y: number; w: number; h: number };

type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/** Resize `start` by a mm delta from one of the 8 handles, snapped + clamped
 *  inside the lane (laneH = the band's explicit height). */
function applyResize(
  start: Box,
  dxMm: number,
  dyMm: number,
  handle: ResizeHandle,
  laneW: number,
  laneH?: number,
): Box {
  let { x, y, w, h } = start;
  if (handle.includes("e")) w = start.w + dxMm;
  if (handle.includes("s")) h = start.h + dyMm;
  if (handle.includes("w")) {
    x = start.x + dxMm;
    w = start.w - dxMm;
  }
  if (handle.includes("n")) {
    y = start.y + dyMm;
    h = start.h - dyMm;
  }
  return clampBox(
    { x: snapMm(x), y: snapMm(y), w: snapMm(Math.max(1, w)), h: snapMm(Math.max(1, h)) },
    laneW,
    1,
    laneH,
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ReportDesignerPage() {
  const { t } = useTranslation();
  const { typeId = "", reportId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("reports", "write");

  const typeQ = useQuery({
    queryKey: ["report-types", typeId],
    queryFn: () => reportTypesApi.get(typeId),
    enabled: !!typeId,
  });
  const reportsQ = useQuery({
    queryKey: ["report-types", typeId, "reports"],
    queryFn: () => reportTypesApi.listReports(typeId),
    enabled: !!typeId,
  });
  const report = (reportsQ.data ?? []).find((r) => r.id === reportId);
  const typeNodes: ReportTypeNode[] = typeQ.data?.schema ?? [];

  // -- layout state + transactional history ---------------------------------
  const [layout, setLayoutState] = React.useState<ReportLayout | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const layoutRef = React.useRef<ReportLayout | null>(null);
  layoutRef.current = layout;
  const past = React.useRef<ReportLayout[]>([]);
  const future = React.useRef<ReportLayout[]>([]);
  const txn = React.useRef<ReportLayout | null>(null);
  const clipboard = React.useRef<ReportControl[]>([]);

  const [sel, setSel] = React.useState<Selection>({ kind: "page" });
  const selRef = React.useRef<Selection>(sel);
  selRef.current = sel;
  const [zoom, setZoom] = React.useState(1);
  const [mode, setMode] = React.useState<"design" | "preview">("design");
  const [rightTab, setRightTab] = React.useState<"props" | "fields">("props");
  const [error, setError] = React.useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = React.useState<string | null>(null);
  const [paramsText, setParamsText] = React.useState("{}");

  // -- preview params (the chicken-and-egg fix) ------------------------------
  // The provider declares a TYPED param contract (`param_schema`, ADR-0062 S1),
  // so the preview bar renders a guided input per param — typed (number →
  // number input) with a required marker — instead of a blind JSON box the
  // operator only reached AFTER a successful render. Values are remembered per
  // report (localStorage). Values are still sent as strings; the backend types
  // them per the declared contract (the S0 keystone — never client-guessed).
  const paramSchema = React.useMemo(() => typeQ.data?.param_schema ?? [], [typeQ.data]);
  const paramNames = React.useMemo(() => paramSchema.map((p) => p.name), [paramSchema]);
  const paramsStorageKey = `tray:report:params:${reportId}`;
  const [paramValues, setParamValues] = React.useState<Record<string, string>>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(`tray:report:params:${reportId}`) ?? "{}");
    } catch {
      return {};
    }
  });
  const setParamValue = (name: string, value: string) => {
    setParamValues((prev) => {
      const next = { ...prev, [name]: value };
      try {
        window.localStorage.setItem(paramsStorageKey, JSON.stringify(next));
      } catch {
        /* storage full/blocked — test values just don't persist */
      }
      return next;
    });
  };
  const [guides, setGuides] = React.useState<{ path: BandPath; v: number[]; h: number[] } | null>(
    null,
  );

  // ---- preview viewer state (the pagination shell posts {pages, page} and
  // takes zoom / goto / print commands over postMessage — opaque origin, so
  // targetOrigin is "*" and the source is checked against OUR frame).
  const previewFrameRef = React.useRef<HTMLIFrameElement>(null);
  const [previewNav, setPreviewNav] = React.useState<{ pages: number; page: number } | null>(null);
  const [previewZoom, setPreviewZoom] = React.useState(1);
  const postToPreview = React.useCallback((msg: Record<string, unknown>) => {
    previewFrameRef.current?.contentWindow?.postMessage(msg, "*");
  }, []);
  React.useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; pages?: number; page?: number } | null;
      if (!d || d.type !== "tray-report:state") return;
      if (e.source !== previewFrameRef.current?.contentWindow) return;
      setPreviewNav({ pages: d.pages ?? 1, page: d.page ?? 1 });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const seededFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (report && seededFor.current !== report.id) {
      seededFor.current = report.id;
      setLayoutState(normalizeLayout(report.layout));
      setDirty(false);
      past.current = [];
      future.current = [];
      setSel({ kind: "page" });
    }
  }, [report]);

  // Unsaved-changes guard (a designer session is real work).
  React.useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  /** The ONE mutation seam: pushes a history entry unless inside a drag
   *  transaction (a drag commits a SINGLE entry at pointer-up). */
  const apply = React.useCallback((fn: (l: ReportLayout) => ReportLayout, history = true) => {
    setLayoutState((l) => {
      if (!l) return l;
      if (history && !txn.current) {
        past.current = [...past.current.slice(-99), l];
        future.current = [];
      }
      return fn(l);
    });
    setDirty(true);
  }, []);
  const applyBands = React.useCallback(
    (fn: (bands: ReportBand[]) => ReportBand[], history = true) =>
      apply((l) => ({ ...l, bands: fn(l.bands) }), history),
    [apply],
  );
  const beginTxn = React.useCallback(() => {
    if (!txn.current) txn.current = layoutRef.current;
  }, []);
  const endTxn = React.useCallback(() => {
    if (txn.current && txn.current !== layoutRef.current) {
      past.current = [...past.current.slice(-99), txn.current];
      future.current = [];
    }
    txn.current = null;
    setGuides(null);
  }, []);
  const undo = React.useCallback(() => {
    const prev = past.current.pop();
    if (!prev || !layoutRef.current) return;
    future.current.push(layoutRef.current);
    setLayoutState(prev);
    setDirty(true);
  }, []);
  const redo = React.useCallback(() => {
    const next = future.current.pop();
    if (!next || !layoutRef.current) return;
    past.current.push(layoutRef.current);
    setLayoutState(next);
    setDirty(true);
  }, []);

  // -- selection-scoped operations ------------------------------------------
  const selectedControls = React.useCallback((): { path: BandPath; indices: number[] } | null => {
    const s = selRef.current;
    return s.kind === "control" && s.indices.length ? { path: s.path, indices: s.indices } : null;
  }, []);

  const deleteSelected = React.useCallback(() => {
    const sc = selectedControls();
    if (!sc) return;
    const idx = new Set(sc.indices);
    applyBands((b) =>
      updateBandAt(b, sc.path, (band) => ({
        ...band,
        controls: band.controls.filter((_, i) => !idx.has(i)),
      })),
    );
    setSel({ kind: "band", path: sc.path });
  }, [applyBands, selectedControls]);

  const copySelected = React.useCallback(
    (cut = false) => {
      const sc = selectedControls();
      const l = layoutRef.current;
      if (!sc || !l) return;
      const band = bandAt(l.bands, sc.path);
      if (!band) return;
      clipboard.current = sc.indices
        .map((i) => band.controls[i])
        .filter(Boolean)
        .map((c) => JSON.parse(JSON.stringify(c)) as ReportControl);
      if (cut) deleteSelected();
    },
    [deleteSelected, selectedControls],
  );

  const pasteClipboard = React.useCallback(() => {
    if (!clipboard.current.length) return;
    const s = selRef.current;
    const path = s.kind !== "page" ? s.path : findFirstDetail(layoutRef.current?.bands ?? []);
    if (!path) return;
    const clones = cloneControls(clipboard.current);
    const before = bandAt(layoutRef.current?.bands ?? [], path)?.controls.length ?? 0;
    applyBands((b) =>
      updateBandAt(b, path, (band) => ({ ...band, controls: [...band.controls, ...clones] })),
    );
    setSel({ kind: "control", path, indices: clones.map((_, i) => before + i) });
  }, [applyBands]);

  const duplicateSelected = React.useCallback(() => {
    copySelected(false);
    pasteClipboard();
  }, [copySelected, pasteClipboard]);

  const nudgeSelected = React.useCallback(
    (dx: number, dy: number) => {
      const sc = selectedControls();
      const l = layoutRef.current;
      if (!sc || !l) return;
      const laneW = laneWidthMm(l.page_setup) - (sc.path.length - 1) * pxToMm(8);
      const idx = new Set(sc.indices);
      applyBands((b) =>
        updateBandAt(b, sc.path, (band) => ({
          ...band,
          controls: band.controls.map((c, i) =>
            idx.has(i)
              ? {
                  ...c,
                  ...clampBox(
                    { x: c.x + dx, y: c.y + dy, w: c.w, h: c.h },
                    laneW,
                    1,
                    band.height_mm,
                  ),
                }
              : c,
          ),
        })),
      );
    },
    [applyBands, selectedControls],
  );

  const alignSelected = React.useCallback(
    (m: AlignMode) => {
      const sc = selectedControls();
      if (!sc || sc.indices.length < 2) return;
      applyBands((b) => alignControls(b, sc.path, sc.indices, m));
    },
    [applyBands, selectedControls],
  );
  const distributeSelected = React.useCallback(
    (axis: "h" | "v") => {
      const sc = selectedControls();
      if (!sc || sc.indices.length < 3) return;
      applyBands((b) => distributeControls(b, sc.path, sc.indices, axis));
    },
    [applyBands, selectedControls],
  );

  // -- keyboard --------------------------------------------------------------
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (mode !== "design" || !canWrite) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest("input, textarea, select, [contenteditable=true]")) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (
        (mod && e.key.toLowerCase() === "y") ||
        (mod && e.shiftKey && e.key.toLowerCase() === "z")
      ) {
        e.preventDefault();
        redo();
      } else if (mod && e.key.toLowerCase() === "c") {
        copySelected(false);
      } else if (mod && e.key.toLowerCase() === "x") {
        copySelected(true);
      } else if (mod && e.key.toLowerCase() === "v") {
        e.preventDefault();
        pasteClipboard();
      } else if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicateSelected();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelected();
      } else if (e.key === "Escape") {
        setSel((s) => (s.kind === "control" ? { kind: "band", path: s.path } : { kind: "page" }));
      } else if (e.key.startsWith("Arrow")) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        const d = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, -step],
          ArrowDown: [0, step],
        }[e.key] as [number, number] | undefined;
        if (d) nudgeSelected(d[0], d[1]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    mode,
    canWrite,
    undo,
    redo,
    copySelected,
    pasteClipboard,
    duplicateSelected,
    deleteSelected,
    nudgeSelected,
  ]);

  // -- persistence + preview -------------------------------------------------
  const save = useMutation({
    mutationFn: () => {
      if (!layoutRef.current) throw new Error("no layout");
      return reportTypesApi.updateReport(typeId, reportId, {
        layout: layoutRef.current as unknown as Record<string, unknown>,
      });
    },
    onSuccess: () => {
      setError(null);
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ["report-types", typeId, "reports"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  // Guided params bind as RAW STRINGS — never client-side type-guessed. The
  // server infers the type from the column (psycopg3 sends a str as "unknown",
  // so `varchar = :p` AND `integer = :p` both work), while a guessed NUMBER
  // against a varchar column is a hard `operator does not exist` (bit live:
  // an all-digit code "401730626" coerced to int vs a varchar cd_ar).
  const buildParams = (): Record<string, unknown> => {
    if (paramNames.length) {
      return Object.fromEntries(paramNames.map((n) => [n, (paramValues[n] ?? "").trim()]));
    }
    try {
      return JSON.parse(paramsText || "{}") as Record<string, unknown>;
    } catch {
      throw new Error(t("reports.badParams", "Parametri: JSON non valido"));
    }
  };
  // Only a REQUIRED param with no value blocks the render (an optional param
  // may be left empty — the backend binds it as an empty string / null).
  const missingParams = paramSchema
    .filter((p) => p.required !== false && !(paramValues[p.name] ?? "").trim())
    .map((p) => p.name);

  const runPreview = useMutation({
    mutationFn: async () => {
      const params = buildParams();
      // The preview is the SAVED document — persist first so the truth matches.
      if (dirty && layoutRef.current) {
        await reportTypesApi.updateReport(typeId, reportId, {
          layout: layoutRef.current as unknown as Record<string, unknown>,
        });
        setDirty(false);
      }
      return reportTypesApi.renderHtml(typeId, reportId, { params });
    },
    onSuccess: (html) => {
      setError(null);
      setPreviewHtml(html);
      setPreviewNav(null); // the fresh shell re-announces {pages, page}
      setMode("preview");
    },
    onError: (e: Error) => setError(e.message),
  });

  // ALWAYS enter preview mode — the params bar lives there, so a failing
  // render must not lock the operator out of the very UI that fixes it
  // (the missing_param chicken-and-egg). With required params still blank,
  // don't fire a doomed render: the placeholder asks for them instead.
  const requestPreview = () => {
    setMode("preview");
    if (missingParams.length) {
      setError(null);
      return;
    }
    runPreview.mutate();
  };

  const downloadPdf = useMutation({
    mutationFn: async () => {
      const params = buildParams();
      await reportTypesApi.renderPdf(typeId, reportId, report?.name ?? "report", { params });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!report || !layout) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {reportsQ.isLoading || typeQ.isLoading
          ? t("common.loading", "Caricamento…")
          : t("reports.notFound", "Report non trovato")}
      </div>
    );
  }

  const laneW = laneWidthMm(layout.page_setup);
  const selectedBand = sel.kind !== "page" ? bandAt(layout.bands, sel.path) : undefined;
  const multi = sel.kind === "control" && sel.indices.length >= 2;

  const addControlToSelection = (kind: ReportControl["kind"]) => {
    const path = sel.kind !== "page" ? sel.path : findFirstDetail(layout.bands);
    if (!path) return;
    const target = bandAt(layout.bands, path);
    // mirror of the backend rule: a break in a running band is meaningless
    // (page header/footer repeat on every page) — the save would 422.
    if (
      kind === "page_break" &&
      (target?.kind === "page_header" || target?.kind === "page_footer")
    ) {
      setError(
        t(
          "reports.pageBreakInRunning",
          "Il salto pagina non è consentito in intestazione/piè di pagina",
        ),
      );
      return;
    }
    const before = target?.controls.length ?? 0;
    applyBands((b) => addControlAt(b, path, newControl(kind)));
    setSel({ kind: "control", path, indices: [before] });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ---------------------------------------------------------- toolbar */}
      <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => navigate("/report-types")}
        >
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          {t("reports.backToTypes", "Report")}
        </Button>
        <div className="max-w-56 truncate text-sm font-medium">
          {typeQ.data?.name} · {report.name}
        </div>
        {dirty && (
          <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] text-warning">
            {t("reports.unsaved", "Non salvato")}
          </span>
        )}

        <div className="mx-2 h-5 w-px bg-border" />
        <ToolbarBtn
          label="Taglia"
          disabled={!canWrite || sel.kind !== "control"}
          onClick={() => copySelected(true)}
        >
          <Scissors className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          label="Copia"
          disabled={sel.kind !== "control"}
          onClick={() => copySelected(false)}
        >
          <Copy className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn label="Incolla" disabled={!canWrite} onClick={pasteClipboard}>
          <ClipboardPaste className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          label="Elimina"
          disabled={!canWrite || sel.kind !== "control"}
          onClick={deleteSelected}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="mx-2 h-5 w-px bg-border" />
        <ToolbarBtn
          label="Annulla (Ctrl+Z)"
          disabled={!canWrite || past.current.length === 0}
          onClick={undo}
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          label="Ripeti (Ctrl+Y)"
          disabled={!canWrite || future.current.length === 0}
          onClick={redo}
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>

        {multi && canWrite && (
          <>
            <div className="mx-2 h-5 w-px bg-border" />
            <ToolbarBtn label="Allinea a sinistra" onClick={() => alignSelected("left")}>
              <AlignStartVertical className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn label="Centra in orizzontale" onClick={() => alignSelected("center_h")}>
              <AlignCenterVertical className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn label="Allinea a destra" onClick={() => alignSelected("right")}>
              <AlignEndVertical className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn label="Allinea in alto" onClick={() => alignSelected("top")}>
              <AlignStartHorizontal className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn label="Centra in verticale" onClick={() => alignSelected("center_v")}>
              <AlignCenterHorizontal className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn label="Allinea in basso" onClick={() => alignSelected("bottom")}>
              <AlignEndHorizontal className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn
              label="Distribuisci orizzontalmente"
              disabled={sel.kind !== "control" || sel.indices.length < 3}
              onClick={() => distributeSelected("h")}
            >
              <StretchHorizontal className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn
              label="Distribuisci verticalmente"
              disabled={sel.kind !== "control" || sel.indices.length < 3}
              onClick={() => distributeSelected("v")}
            >
              <StretchVertical className="h-3.5 w-3.5" />
            </ToolbarBtn>
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          <ToolbarBtn
            label="Riduci zoom"
            onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))}
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </ToolbarBtn>
          <select
            value={String(zoom)}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom"
            className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
          >
            {[0.5, 0.75, 1, 1.25, 1.5, 2].map((z) => (
              <option key={z} value={z}>
                {Math.round(z * 100)}%
              </option>
            ))}
          </select>
          <ToolbarBtn
            label="Aumenta zoom"
            onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.25) * 100) / 100))}
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </ToolbarBtn>

          <div className="mx-2 h-5 w-px bg-border" />
          {canWrite && (
            <Button
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => save.mutate()}
              disabled={save.isPending || !dirty}
            >
              {t("common.save", "Salva")}
            </Button>
          )}
          <div className="ml-1 flex overflow-hidden rounded-md border border-border">
            <button
              type="button"
              onClick={() => setMode("design")}
              className={cn(
                "px-3 py-1 text-xs",
                mode === "design"
                  ? "bg-foreground text-background"
                  : "bg-surface text-muted-foreground",
              )}
            >
              {t("reports.design", "Design")}
            </button>
            <button
              type="button"
              onClick={requestPreview}
              disabled={runPreview.isPending}
              className={cn(
                "px-3 py-1 text-xs",
                mode === "preview"
                  ? "bg-foreground text-background"
                  : "bg-surface text-muted-foreground",
              )}
            >
              {t("reports.previewHtml", "Anteprima")}
            </button>
          </div>
        </div>
      </div>
      {error && (
        <div className="border-b border-danger/40 bg-danger/10 px-3 py-1.5 text-xs text-danger">
          {error}
        </div>
      )}

      {/* ------------------------------------------------------------- body */}
      {mode === "preview" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-1.5">
            {paramSchema.length > 0 ? (
              // Guided TYPED form: one input per provider param (sql :binds),
              // the HTML input type driven by the declared param type (number →
              // number field), a `*` on required. The free-form JSON box
              // survives only for param-less providers (a `code` provider reads
              // arbitrary ctx.input keys).
              paramSchema.map((p) => (
                <label
                  key={p.name}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <span className="font-mono">
                    {p.name}
                    {p.required !== false && <span className="text-danger">*</span>}
                  </span>
                  <Input
                    type={p.type === "number" ? "number" : "text"}
                    value={paramValues[p.name] ?? ""}
                    onChange={(e) => setParamValue(p.name, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") requestPreview();
                    }}
                    placeholder={p.description || t("reports.paramValue", "valore")}
                    className="h-7 w-40 font-mono text-xs"
                  />
                </label>
              ))
            ) : (
              <>
                <span className="text-xs text-muted-foreground">
                  {t("reports.runParams", "Parametri (JSON)")}
                </span>
                <Input
                  value={paramsText}
                  onChange={(e) => setParamsText(e.target.value)}
                  className="h-7 max-w-md font-mono text-xs"
                />
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={requestPreview}
              disabled={runPreview.isPending || missingParams.length > 0}
            >
              {t("reports.refresh", "Aggiorna")}
            </Button>

            {/* viewer toolbar — page nav · zoom · print · PDF */}
            <div className="ml-auto flex items-center gap-1">
              <ToolbarBtn
                label={t("reports.prevPage", "Pagina precedente")}
                disabled={!previewNav || previewNav.page <= 1}
                onClick={() =>
                  postToPreview({ type: "tray-report:goto", page: (previewNav?.page ?? 1) - 1 })
                }
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </ToolbarBtn>
              <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground">
                {previewNav ? `${previewNav.page} / ${previewNav.pages}` : "–"}
              </span>
              <ToolbarBtn
                label={t("reports.nextPage", "Pagina successiva")}
                disabled={!previewNav || previewNav.page >= previewNav.pages}
                onClick={() =>
                  postToPreview({ type: "tray-report:goto", page: (previewNav?.page ?? 1) + 1 })
                }
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </ToolbarBtn>
              <div className="mx-1.5 h-5 w-px bg-border" />
              <select
                value={previewZoom}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setPreviewZoom(v);
                  postToPreview({ type: "tray-report:zoom", value: v });
                }}
                aria-label={t("reports.previewZoom", "Zoom anteprima")}
                className="h-7 rounded-md border border-border bg-surface px-1 text-xs"
              >
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((z) => (
                  <option key={z} value={z}>
                    {Math.round(z * 100)}%
                  </option>
                ))}
              </select>
              <div className="mx-1.5 h-5 w-px bg-border" />
              <ToolbarBtn
                label={t("reports.print", "Stampa")}
                disabled={previewHtml === null}
                onClick={() => postToPreview({ type: "tray-report:print" })}
              >
                <Printer className="h-3.5 w-3.5" />
              </ToolbarBtn>
              <ToolbarBtn
                label={t("reports.downloadPdf", "Scarica PDF")}
                disabled={downloadPdf.isPending || missingParams.length > 0}
                onClick={() => downloadPdf.mutate()}
              >
                <Download className="h-3.5 w-3.5" />
              </ToolbarBtn>
            </div>
          </div>
          {previewHtml !== null ? (
            // allow-scripts: the compiler's pagination shell must run;
            // allow-modals: window.print() needs it. NO allow-same-origin —
            // the frame stays an opaque origin.
            <iframe
              ref={previewFrameRef}
              title={t("reports.previewTitle", "Anteprima report")}
              srcDoc={previewHtml}
              sandbox="allow-scripts allow-modals"
              onLoad={() => {
                if (previewZoom !== 1)
                  postToPreview({ type: "tray-report:zoom", value: previewZoom });
              }}
              className="min-h-0 w-full flex-1 bg-[#e8e8e8]"
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {runPreview.isPending
                ? t("common.loading", "Caricamento…")
                : missingParams.length > 0
                  ? t(
                      "reports.fillParams",
                      "Inserisci i parametri richiesti dal report e premi Aggiorna",
                    )
                  : t("reports.pressRefresh", "Premi Aggiorna per generare l'anteprima")}
            </div>
          )}
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[44px_1fr_320px_40px]">
          {/* toolbox */}
          <Toolbox canWrite={canWrite} onAdd={addControlToSelection} />

          {/* canvas */}
          <div
            className="min-h-0 overflow-auto bg-surface/60 p-4"
            role="presentation"
            onClick={() => setSel({ kind: "page" })}
          >
            {/* mx-auto + w-fit centers the sheet like DevExpress, and degrades
                to left-aligned scrolling when the zoomed page exceeds the
                viewport (flex justify-center would CLIP the left edge). */}
            <div className="mx-auto w-fit">
              <Ruler laneW={laneW} zoom={zoom} />
              <BandList
                bands={layout.bands}
                parentPath={[]}
                scope={typeNodes}
                laneW={laneW}
                zoom={zoom}
                sel={sel}
                canWrite={canWrite}
                guides={guides}
                onGuides={setGuides}
                onSelect={setSel}
                onPatchBands={applyBands}
                beginTxn={beginTxn}
                endTxn={endTxn}
                nestedKinds={TOP_BAND_KINDS}
              />
            </div>
          </div>

          {/* right panel + vertical tab strip */}
          <div className="min-h-0 overflow-y-auto border-l border-border">
            {rightTab === "fields" ? (
              <FieldsPanel nodes={typeNodes} />
            ) : (
              <InspectorPanel
                layout={layout}
                sel={sel}
                selectedBand={selectedBand}
                typeNodes={typeNodes}
                bands={layout.bands}
                canWrite={canWrite}
                onPatchLayout={apply}
                onPatchBands={applyBands}
                onSelect={setSel}
              />
            )}
          </div>
          <div className="flex flex-col items-center gap-1 border-l border-border bg-surface py-2">
            <button
              type="button"
              title={t("reports.propsTab", "Proprietà")}
              onClick={() => setRightTab("props")}
              className={cn(
                "rounded p-1.5",
                rightTab === "props"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-elevated",
              )}
            >
              <Settings2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              title={t("reports.fieldsTab", "Campi")}
              onClick={() => setRightTab("fields")}
              className={cn(
                "rounded p-1.5",
                rightTab === "fields"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-elevated",
              )}
            >
              <Database className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolbarBtn({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded p-1.5 text-muted-foreground hover:bg-surface disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function findFirstDetail(bands: ReportBand[]): BandPath | null {
  const idx = bands.findIndex((b) => b.kind === "detail");
  if (idx >= 0) return [idx];
  return bands.length ? [0] : null;
}

// ---------------------------------------------------------------------------
// Toolbox (left icon strip) — click adds to the selected band; drag onto a lane
// ---------------------------------------------------------------------------

const TOOLBOX: { kind: ReportControl["kind"]; label: string; icon: React.ReactNode }[] = [
  { kind: "label", label: "Etichetta", icon: <TypeIcon className="h-4 w-4" /> },
  { kind: "field", label: "Campo", icon: <FileType2 className="h-4 w-4" /> },
  { kind: "table", label: "Tabella", icon: <TableIcon className="h-4 w-4" /> },
  { kind: "image", label: "Immagine", icon: <ImageIcon className="h-4 w-4" /> },
  { kind: "line", label: "Linea", icon: <Minus className="h-4 w-4" /> },
  { kind: "shape", label: "Riquadro", icon: <Square className="h-4 w-4" /> },
  { kind: "rich_text", label: "Testo", icon: <Pilcrow className="h-4 w-4" /> },
  { kind: "checkbox", label: "Checkbox", icon: <CheckSquare className="h-4 w-4" /> },
  { kind: "barcode", label: "Barcode / QR", icon: <QrCode className="h-4 w-4" /> },
  { kind: "page_info", label: "Info pagina", icon: <Hash className="h-4 w-4" /> },
  { kind: "page_break", label: "Salto pagina", icon: <SeparatorHorizontal className="h-4 w-4" /> },
];

function Toolbox({
  canWrite,
  onAdd,
}: {
  canWrite: boolean;
  onAdd: (k: ReportControl["kind"]) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1 border-r border-border bg-surface py-2">
      {TOOLBOX.map((tool) => (
        <button
          key={tool.kind}
          type="button"
          title={tool.label}
          aria-label={tool.label}
          disabled={!canWrite}
          draggable={canWrite}
          onDragStart={(e) => {
            e.dataTransfer.setData(CTL_MIME, tool.kind);
            e.dataTransfer.effectAllowed = "copy";
          }}
          onClick={() => onAdd(tool.kind)}
          className="rounded p-1.5 text-muted-foreground hover:bg-elevated hover:text-foreground disabled:opacity-35"
        >
          {tool.icon}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ruler
// ---------------------------------------------------------------------------

function Ruler({ laneW, zoom }: { laneW: number; zoom: number }) {
  const ticks = rulerTicks(laneW);
  return (
    <div
      className="relative mb-1 h-5 select-none"
      style={{ marginLeft: STRIP_W + VRULER_W, width: mmToPx(laneW, zoom) + 2 }}
    >
      <div className="absolute inset-0 rounded-sm bg-amber-100/70 dark:bg-amber-950/40" />
      {ticks.map((tk) => (
        <div key={tk.mm} className="absolute bottom-0" style={{ left: mmToPx(tk.mm, zoom) }}>
          <div className={cn("w-px bg-foreground/40", tk.major ? "h-2.5" : "h-1.5")} />
          {tk.major && tk.mm > 0 && (
            <span className="absolute -top-0.5 left-0.5 text-[8px] text-muted-foreground">
              {tk.mm}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Per-lane vertical mm ruler (the horizontal ruler's twin). One per band —
 *  a band's y origin is ITS OWN top (the print model), so a single continuous
 *  page ruler would lie about coordinates. */
function VRuler({ heightMm, zoom }: { heightMm: number; zoom: number }) {
  const ticks = rulerTicks(heightMm);
  return (
    <div
      className="relative shrink-0 select-none"
      style={{ width: VRULER_W, height: mmToPx(heightMm, zoom) + 2 }}
    >
      <div className="absolute inset-0 rounded-sm bg-amber-100/70 dark:bg-amber-950/40" />
      {ticks.map((tk) => (
        <div key={tk.mm} className="absolute right-0" style={{ top: mmToPx(tk.mm, zoom) }}>
          <div className={cn("ml-auto h-px bg-foreground/40", tk.major ? "w-2.5" : "w-1.5")} />
          {tk.major && tk.mm > 0 && (
            <span className="absolute left-0.5 top-0 text-[7px] leading-none text-muted-foreground">
              {tk.mm}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canvas — recursive band lanes with rotated band strips
// ---------------------------------------------------------------------------

function BandList({
  bands,
  parentPath,
  scope,
  laneW,
  zoom,
  sel,
  canWrite,
  guides,
  onGuides,
  onSelect,
  onPatchBands,
  beginTxn,
  endTxn,
  nestedKinds,
}: {
  bands: ReportBand[];
  parentPath: BandPath;
  scope: ReportTypeNode[];
  laneW: number;
  zoom: number;
  sel: Selection;
  canWrite: boolean;
  guides: { path: BandPath; v: number[]; h: number[] } | null;
  onGuides: (g: { path: BandPath; v: number[]; h: number[] } | null) => void;
  onSelect: (s: Selection) => void;
  onPatchBands: (fn: (bands: ReportBand[]) => ReportBand[], history?: boolean) => void;
  beginTxn: () => void;
  endTxn: () => void;
  nestedKinds: BandKind[];
}) {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = React.useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      {bands.map((band, i) => {
        const path = [...parentPath, i];
        const childScope =
          band.kind === "detail_report"
            ? (scope.find((n) => n.id === band.bind && n.type === "collection")?.children ?? [])
            : scope;
        // Cross-band alignment guides: controls of the SIBLING bands at this
        // level (same lane x origin) — vertical guides only in the snapper.
        const crossBoxes = bands.flatMap((b, j) =>
          j === i
            ? []
            : b.controls
                .filter((c) => c.kind !== "page_break")
                .map((c) => ({ x: c.x, y: c.y, w: c.w, h: c.h })),
        );
        return (
          <BandLane
            key={i}
            band={band}
            path={path}
            crossBoxes={crossBoxes}
            scope={scope}
            laneW={laneW}
            zoom={zoom}
            sel={sel}
            canWrite={canWrite}
            guides={guides}
            onGuides={onGuides}
            onSelect={onSelect}
            onPatchBands={onPatchBands}
            beginTxn={beginTxn}
            endTxn={endTxn}
          >
            {band.kind === "detail_report" && (
              <div
                className="border-l-2 border-dashed border-accent/40 pl-2 pt-1.5"
                style={{ marginLeft: STRIP_W + VRULER_W }}
              >
                <BandList
                  bands={band.bands ?? []}
                  parentPath={path}
                  scope={childScope}
                  laneW={laneW - 8}
                  zoom={zoom}
                  sel={sel}
                  canWrite={canWrite}
                  guides={guides}
                  onGuides={onGuides}
                  onSelect={onSelect}
                  onPatchBands={onPatchBands}
                  beginTxn={beginTxn}
                  endTxn={endTxn}
                  nestedKinds={NESTED_BAND_KINDS}
                />
              </div>
            )}
          </BandLane>
        );
      })}
      {canWrite && (
        <div
          className="relative pt-1"
          role="presentation"
          style={{ marginLeft: STRIP_W + VRULER_W }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setAddOpen((o) => !o)}
          >
            <Plus className="mr-0.5 h-3 w-3" />
            {t("reports.addBand", "Banda")}
          </Button>
          {addOpen && (
            <>
              <div
                role="presentation"
                className="fixed inset-0 z-30"
                onClick={() => setAddOpen(false)}
              />
              <div className="absolute bottom-8 left-0 z-40 w-72 rounded-md border border-border bg-elevated py-1 shadow-lg">
                {nestedKinds.map((k) => {
                  // header/footer bands are SINGLETONS per level; only
                  // detail / detail_report may repeat.
                  const singleton = k !== "detail" && k !== "detail_report";
                  const taken = singleton && bands.some((b) => b.kind === k);
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={taken}
                      onClick={() => {
                        setAddOpen(false);
                        const band = newBand(k);
                        // canonical order: compute the landing index from the
                        // CURRENT bands so the new band can be selected.
                        const next = sortBands([...bands, band]);
                        const idx = next.indexOf(band);
                        if (parentPath.length === 0) onPatchBands((b) => sortBands([...b, band]));
                        else
                          onPatchBands((b) =>
                            updateBandAt(b, parentPath, (p) => ({
                              ...p,
                              bands: sortBands([...(p.bands ?? []), band]),
                            })),
                          );
                        onSelect({ kind: "band", path: [...parentPath, idx] });
                      }}
                      className={cn(
                        "flex w-full flex-col items-start gap-0 px-3 py-1.5 text-left hover:bg-surface",
                        taken && "cursor-not-allowed opacity-40 hover:bg-transparent",
                      )}
                    >
                      <span className="text-xs font-medium">{BAND_LABELS[k]}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {taken ? t("reports.bandTaken", "Già presente") : BAND_HINTS[k]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BandLane({
  band,
  path,
  scope,
  laneW,
  zoom,
  sel,
  canWrite,
  guides,
  onGuides,
  onSelect,
  onPatchBands,
  beginTxn,
  endTxn,
  crossBoxes,
  children,
}: {
  band: ReportBand;
  path: BandPath;
  scope: ReportTypeNode[];
  laneW: number;
  zoom: number;
  sel: Selection;
  canWrite: boolean;
  guides: { path: BandPath; v: number[]; h: number[] } | null;
  onGuides: (g: { path: BandPath; v: number[]; h: number[] } | null) => void;
  onSelect: (s: Selection) => void;
  onPatchBands: (fn: (bands: ReportBand[]) => ReportBand[], history?: boolean) => void;
  beginTxn: () => void;
  endTxn: () => void;
  crossBoxes?: { x: number; y: number; w: number; h: number }[];
  children?: React.ReactNode;
}) {
  const laneRef = React.useRef<HTMLDivElement>(null);
  // Band-height resize via the bottom grab strip (DevExpress model): the
  // height is an EXPLICIT property — controls are clamped inside it, so the
  // lane never stretches elastically during a control drag. Min = the
  // controls' bottom edge (shrinking can never hide a control).
  const bandResizeRef = React.useRef<{ startY: number; startH: number } | null>(null);
  const onBandResizeDown = (e: React.PointerEvent) => {
    if (!canWrite) return;
    e.stopPropagation();
    onSelect({ kind: "band", path });
    bandResizeRef.current = { startY: e.clientY, startH: band.height_mm };
    beginTxn();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onBandResizeMove = (e: React.PointerEvent) => {
    const d = bandResizeRef.current;
    if (!d) return;
    const minH = Math.max(1, ...band.controls.map((c) => c.y + c.h));
    const h = Math.max(minH, snapMm(d.startH + pxToMm(e.clientY - d.startY, zoom)));
    onPatchBands((b) => updateBandAt(b, path, { height_mm: h }), false);
  };
  const onBandResizeUp = (e: React.PointerEvent) => {
    if (!bandResizeRef.current) return;
    e.stopPropagation();
    bandResizeRef.current = null;
    endTxn();
  };
  const isBandSel = sel.kind === "band" && samePath(sel.path, path);
  const selectedIdx =
    sel.kind === "control" && samePath(sel.path, path) ? new Set(sel.indices) : new Set<number>();
  const gridPx = mmToPx(5, zoom);
  const laneGuides = guides && samePath(guides.path, path) ? guides : null;

  // Marquee select on empty lane space (pointer drag > threshold).
  const marqueeRef = React.useRef<{ x0: number; y0: number } | null>(null);
  const [marquee, setMarquee] = React.useState<Box | null>(null);

  const laneMm = (e: React.PointerEvent | React.DragEvent): { x: number; y: number } => {
    const rect = laneRef.current!.getBoundingClientRect();
    return { x: pxToMm(e.clientX - rect.left, zoom), y: pxToMm(e.clientY - rect.top, zoom) };
  };

  const onLanePointerDown = (e: React.PointerEvent) => {
    if (e.target !== laneRef.current) return;
    e.stopPropagation();
    const p = laneMm(e);
    marqueeRef.current = { x0: p.x, y0: p.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onLanePointerMove = (e: React.PointerEvent) => {
    const m = marqueeRef.current;
    if (!m) return;
    const p = laneMm(e);
    setMarquee({
      x: Math.min(m.x0, p.x),
      y: Math.min(m.y0, p.y),
      w: Math.abs(p.x - m.x0),
      h: Math.abs(p.y - m.y0),
    });
  };
  const onLanePointerUp = () => {
    // Only act if the gesture STARTED on the empty lane (onLanePointerDown
    // armed the marquee). A pointerup bubbling up from a control click/drag
    // must NOT steal the selection back to the band — the reported bug.
    if (marqueeRef.current === null) return;
    const m = marquee;
    marqueeRef.current = null;
    setMarquee(null);
    if (!m || (m.w < 1 && m.h < 1)) {
      onSelect({ kind: "band", path });
      return;
    }
    const hit = band.controls
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.x < m.x + m.w && c.x + c.w > m.x && c.y < m.y + m.h && c.y + c.h > m.y)
      .map(({ i }) => i);
    onSelect(hit.length ? { kind: "control", path, indices: hit } : { kind: "band", path });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canWrite) return;
    const p = laneMm(e);
    const x = Math.max(0, Math.round(p.x));
    const y = Math.max(0, Math.round(p.y));
    const before = band.controls.length;

    const ctlKind = e.dataTransfer.getData(CTL_MIME);
    if (ctlKind) {
      // mirror of the backend rule: no page_break in a running band.
      if (ctlKind === "page_break" && (band.kind === "page_header" || band.kind === "page_footer"))
        return;
      onPatchBands((b) =>
        addControlAt(b, path, newControl(ctlKind as ReportControl["kind"], x, y)),
      );
      onSelect({ kind: "control", path, indices: [before] });
      return;
    }
    const raw = e.dataTransfer.getData(FIELD_MIME);
    if (!raw) return;
    let payload: { id: string };
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    // Scope guard — a field outside this band's scope is refused early
    // (mirror of the backend referential rule; the save would 422 anyway).
    // The lookup is over the FLATTENED scope so a field nested in an object
    // (dragged as its dotted path `test.campo_3`) is a first-class target.
    const entry = flattenScope(scope).find((s) => s.id === payload.id);
    if (!entry) return;
    const ctl: ReportControl =
      entry.node.type === "collection"
        ? tableFromCollection(entry.node, x, y, entry.id)
        : ({ ...newControl("field", x, y), bind: entry.id } as FieldControl);
    onPatchBands((b) => addControlAt(b, path, ctl));
    onSelect({ kind: "control", path, indices: [before] });
  };

  return (
    <div>
      <div className="flex">
        {/* rotated band strip OUTERMOST, then the vertical ruler, then the
            canvas — the DevExpress column order (owner's call). */}
        <button
          type="button"
          title={`${BAND_LABELS[band.kind]} · ${band.height_mm}mm`}
          onClick={(e) => {
            e.stopPropagation();
            onSelect({ kind: "band", path });
          }}
          className={cn(
            // The rotated label is ABSOLUTE (out of flow): its intrinsic
            // vertical length must NOT stretch the flex row — that inflated
            // short bands past their real mm and left the vertical ruler
            // "short" (the ruler was right, the lane was lying).
            "relative shrink-0 overflow-hidden rounded-l border border-border",
            BAND_TINT[band.kind],
            isBandSel && "ring-2 ring-accent",
          )}
          style={{ width: STRIP_W }}
        >
          <span
            className="absolute inset-0 flex items-center justify-center whitespace-nowrap text-[9px] uppercase tracking-wider text-foreground/70"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {BAND_LABELS[band.kind]}
            {band.kind === "detail_report" && band.bind ? ` · ${band.bind}` : ""}
          </span>
        </button>

        <VRuler heightMm={bandVisualHeightMm(band)} zoom={zoom} />

        <div
          ref={laneRef}
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={onLanePointerDown}
          onPointerMove={onLanePointerMove}
          onPointerUp={onLanePointerUp}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDrop={handleDrop}
          className={cn(
            // The lane is PAPER: white in BOTH themes (matches the preview
            // sheet — the print truth), so control text is forced dark too.
            "relative rounded-r border border-border bg-white text-neutral-900",
            isBandSel && "ring-2 ring-accent",
          )}
          style={{
            width: mmToPx(laneW, zoom) + 2,
            // auto-grow lanes grow to the controls' bottom edge (the compiler's
            // min-height formula) so a control dragged below height_mm never
            // vanishes under the next lane; fixed bands clip, like print.
            minHeight: mmToPx(bandVisualHeightMm(band), zoom),
            ...(band.auto_grow === false
              ? { height: mmToPx(band.height_mm, zoom), overflow: "hidden" }
              : {}),
            backgroundImage:
              "linear-gradient(to right, rgba(120,120,120,0.12) 1px, transparent 1px)," +
              "linear-gradient(to bottom, rgba(120,120,120,0.12) 1px, transparent 1px)",
            backgroundSize: `${gridPx}px ${gridPx}px`,
            touchAction: "none",
          }}
        >
          {band.controls.map((ctl, i) => (
            <ControlBox
              key={i}
              band={band}
              ctl={ctl}
              index={i}
              path={path}
              selectedIdx={selectedIdx}
              zoom={zoom}
              laneW={laneW}
              canWrite={canWrite}
              crossBoxes={crossBoxes}
              onSelect={onSelect}
              onPatchBands={onPatchBands}
              onGuides={onGuides}
              beginTxn={beginTxn}
              endTxn={endTxn}
            />
          ))}
          {/* smart guides */}
          {laneGuides?.v.map((x) => (
            <div
              key={`v${x}`}
              className="pointer-events-none absolute bottom-0 top-0 w-px bg-danger/70"
              style={{ left: mmToPx(x, zoom) }}
            />
          ))}
          {laneGuides?.h.map((y) => (
            <div
              key={`h${y}`}
              className="pointer-events-none absolute left-0 right-0 h-px bg-danger/70"
              style={{ top: mmToPx(y, zoom) }}
            />
          ))}
          {/* marquee */}
          {marquee && (
            <div
              className="pointer-events-none absolute border border-accent bg-accent/10"
              style={{
                left: mmToPx(marquee.x, zoom),
                top: mmToPx(marquee.y, zoom),
                width: mmToPx(marquee.w, zoom),
                height: mmToPx(marquee.h, zoom),
              }}
            />
          )}
          {/* band-height resize handle (bottom grab strip) */}
          {canWrite && (
            <div
              role="presentation"
              title={`Altezza banda · ${band.height_mm}mm`}
              onPointerDown={onBandResizeDown}
              onPointerMove={onBandResizeMove}
              onPointerUp={onBandResizeUp}
              className="absolute inset-x-0 bottom-0 z-20 h-1.5 cursor-ns-resize border-b-2 border-transparent hover:border-accent/70 active:border-accent"
              style={{ touchAction: "none" }}
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Control box — multi-drag, 8 resize handles, smart-guide snapping
// ---------------------------------------------------------------------------

function controlCaption(ctl: ReportControl): string {
  switch (ctl.kind) {
    case "label":
      return ctl.text || "label";
    case "field":
      return `[${ctl.bind || "?"}]${ctl.agg ? ` Σ${ctl.agg}` : ""}`;
    case "image":
      return ctl.bind ? `img [${ctl.bind}]` : "img";
    case "line":
    case "shape":
      return "";
    case "page_info":
      return ctl.info === "print_date" ? "data stampa" : "pag. N";
    case "page_break":
      return "salto pagina";
    case "checkbox":
      return `☑ [${ctl.bind || "?"}]${ctl.text ? ` ${ctl.text}` : ""}`;
    case "rich_text":
      return ctl.text || "testo";
    case "barcode":
      return `${(ctl.symbology ?? "qr").toUpperCase()} ${ctl.bind ? `[${ctl.bind}]` : (ctl.value ?? "")}`;
    case "table":
      return `tabella [${ctl.bind || "?"}]`;
  }
}

const HANDLES: { h: ResizeHandle; cls: string; cursor: string }[] = [
  { h: "nw", cls: "-left-1 -top-1", cursor: "nwse-resize" },
  { h: "n", cls: "-top-1 left-1/2 -translate-x-1/2", cursor: "ns-resize" },
  { h: "ne", cls: "-right-1 -top-1", cursor: "nesw-resize" },
  { h: "e", cls: "-right-1 top-1/2 -translate-y-1/2", cursor: "ew-resize" },
  { h: "se", cls: "-bottom-1 -right-1", cursor: "nwse-resize" },
  { h: "s", cls: "-bottom-1 left-1/2 -translate-x-1/2", cursor: "ns-resize" },
  { h: "sw", cls: "-bottom-1 -left-1", cursor: "nesw-resize" },
  { h: "w", cls: "-left-1 top-1/2 -translate-y-1/2", cursor: "ew-resize" },
];

function ControlBox({
  band,
  ctl,
  index,
  path,
  selectedIdx,
  zoom,
  laneW,
  canWrite,
  crossBoxes,
  onSelect,
  onPatchBands,
  onGuides,
  beginTxn,
  endTxn,
}: {
  band: ReportBand;
  ctl: ReportControl;
  index: number;
  path: BandPath;
  selectedIdx: Set<number>;
  zoom: number;
  laneW: number;
  canWrite: boolean;
  crossBoxes?: { x: number; y: number; w: number; h: number }[];
  onSelect: (s: Selection) => void;
  onPatchBands: (fn: (bands: ReportBand[]) => ReportBand[], history?: boolean) => void;
  onGuides: (g: { path: BandPath; v: number[]; h: number[] } | null) => void;
  beginTxn: () => void;
  endTxn: () => void;
}) {
  const selected = selectedIdx.has(index);
  const single = selected && selectedIdx.size === 1;
  const dragRef = React.useRef<{
    mode: "move" | ResizeHandle;
    startX: number;
    startY: number;
    boxes: Map<number, Box>;
    moved: boolean;
  } | null>(null);

  const begin = (e: React.PointerEvent, mode: "move" | ResizeHandle) => {
    e.stopPropagation();
    // Shift-click toggles membership; plain click selects (keeps a multi
    // selection intact when grabbing one of its members to drag).
    const already = selectedIdx.has(index);
    let indices: number[];
    if (e.shiftKey) {
      indices = already ? [...selectedIdx].filter((i) => i !== index) : [...selectedIdx, index];
      if (!indices.length) indices = [index];
    } else {
      indices = already ? [...selectedIdx] : [index];
    }
    onSelect({ kind: "control", path, indices });
    if (!canWrite) return;
    const boxes = new Map<number, Box>();
    for (const i of indices) {
      const c = band.controls[i];
      if (c) boxes.set(i, { x: c.x, y: c.y, w: c.w, h: c.h });
    }
    if (!boxes.has(index)) boxes.set(index, { x: ctl.x, y: ctl.y, w: ctl.w, h: ctl.h });
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, boxes, moved: false };
    beginTxn();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const move = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dxMm = pxToMm(e.clientX - d.startX, zoom);
    const dyMm = pxToMm(e.clientY - d.startY, zoom);
    if (Math.abs(dxMm) < 0.3 && Math.abs(dyMm) < 0.3 && !d.moved) return;
    d.moved = true;

    if (d.mode === "move") {
      const primaryStart = d.boxes.get(index)!;
      let primary: Box = {
        ...primaryStart,
        x: snapMm(primaryStart.x + dxMm),
        y: snapMm(primaryStart.y + dyMm),
      };
      // Smart guides against NON-selected siblings, PLUS the sibling bands'
      // controls (same x origin → vertical guides only, cross-band).
      const siblings = band.controls
        .filter((_, i) => !d.boxes.has(i))
        .map((c) => ({ x: c.x, y: c.y, w: c.w, h: c.h }));
      const snapped = snapToSiblings(primary, siblings, 0.8, crossBoxes ?? []);
      primary = clampBox(snapped.box, laneW, 1, band.height_mm);
      onGuides(
        snapped.vGuides.length || snapped.hGuides.length
          ? { path, v: snapped.vGuides, h: snapped.hGuides }
          : null,
      );
      const ddx = primary.x - primaryStart.x;
      const ddy = primary.y - primaryStart.y;
      onPatchBands(
        (b) =>
          updateBandAt(b, path, (bb) => ({
            ...bb,
            controls: bb.controls.map((c, i) => {
              const st = d.boxes.get(i);
              if (!st) return c;
              return {
                ...c,
                ...clampBox({ ...st, x: st.x + ddx, y: st.y + ddy }, laneW, 1, bb.height_mm),
              };
            }),
          })),
        false,
      );
    } else {
      const st = d.boxes.get(index)!;
      const box = applyResize(st, dxMm, dyMm, d.mode, laneW, band.height_mm);
      onPatchBands((b) => updateControlAt(b, path, index, box), false);
    }
  };

  const end = (e: React.PointerEvent) => {
    e.stopPropagation(); // don't let the lane's marquee handler see this up
    dragRef.current = null;
    onGuides(null);
    endTxn();
  };

  const s = ctl.style ?? {};
  return (
    <div
      role="presentation"
      onPointerDown={(e) => begin(e, "move")}
      onPointerMove={move}
      onPointerUp={end}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        // Paper-fixed neutrals (the lane is white in both themes) — theme
        // tokens like border-border/bg-elevated would go dark-on-white in
        // dark mode and disappear.
        "absolute flex cursor-move items-center overflow-visible border text-[10px] leading-tight",
        selected
          ? "z-10 border-accent bg-accent/10"
          : "border-neutral-400/60 bg-neutral-900/[0.04]",
        ctl.kind === "line" && "justify-center border-dashed",
        ctl.kind === "page_break" && "justify-center border-dashed border-danger/60 bg-danger/5",
        ctl.kind === "rich_text" && "items-start",
      )}
      style={{
        left: mmToPx(ctl.x, zoom),
        top: mmToPx(ctl.y, zoom),
        width: mmToPx(ctl.w, zoom),
        height: mmToPx(ctl.h, zoom),
        fontWeight: s.bold ? 600 : undefined,
        fontStyle: s.italic ? "italic" : undefined,
        textAlign: (s.align as React.CSSProperties["textAlign"]) ?? undefined,
        color: s.color ?? undefined,
        background: s.background ?? undefined,
        touchAction: "none",
      }}
      title={controlCaption(ctl)}
    >
      {ctl.kind === "line" ? (
        <div
          className="bg-neutral-900/60"
          style={
            (ctl.direction ?? "horizontal") === "horizontal"
              ? { width: "100%", height: 1 }
              : { height: "100%", width: 1 }
          }
        />
      ) : ctl.kind === "page_break" ? (
        <span className="w-full truncate px-1 text-center text-[9px] uppercase tracking-wider text-danger/80">
          ✂ salto pagina
        </span>
      ) : ctl.kind === "rich_text" ? (
        <span className="h-full w-full overflow-hidden whitespace-pre-wrap px-1">{ctl.text}</span>
      ) : ctl.kind === "barcode" ? (
        <span className="flex w-full items-center justify-center gap-1 truncate px-1 text-neutral-500">
          <QrCode className="h-3.5 w-3.5 shrink-0" />
          {controlCaption(ctl)}
        </span>
      ) : (
        <span className="w-full truncate px-1">{controlCaption(ctl)}</span>
      )}
      {single &&
        canWrite &&
        HANDLES.map((hd) => (
          <div
            key={hd.h}
            role="presentation"
            onPointerDown={(e) => begin(e, hd.h)}
            onPointerMove={move}
            onPointerUp={end}
            className={cn(
              "absolute h-2 w-2 rounded-[2px] border border-background bg-accent",
              hd.cls,
            )}
            style={{ cursor: hd.cursor, touchAction: "none" }}
          />
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fields panel (right tab) — searchable Type tree, draggable
// ---------------------------------------------------------------------------

function FieldsPanel({ nodes }: { nodes: ReportTypeNode[] }) {
  const { t } = useTranslation();
  const [q, setQ] = React.useState("");

  const filter = (items: ReportTypeNode[]): ReportTypeNode[] => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    const walk = (list: ReportTypeNode[]): ReportTypeNode[] =>
      list
        .map((n) => {
          const kids = n.children ? walk(n.children) : undefined;
          const hit =
            n.id.toLowerCase().includes(needle) || (n.label ?? "").toLowerCase().includes(needle);
          if (hit) return n;
          if (kids && kids.length) return { ...n, children: kids };
          return null;
        })
        .filter((n): n is ReportTypeNode => n !== null);
    return walk(items);
  };
  const filtered = filter(nodes);

  return (
    <div className="flex min-h-0 flex-col">
      <div className="border-b border-border p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("reports.searchFields", "Cerca campo…")}
            className="h-7 pl-7 text-xs"
          />
        </div>
      </div>
      <div className="p-2">
        {filtered.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">
            {t("reports.noFields", "Nessun campo.")}
          </p>
        ) : (
          <FieldTree nodes={filtered} depth={0} />
        )}
      </div>
      <p className="mt-auto border-t border-border p-2 text-[11px] text-muted-foreground">
        {t("reports.dragHint", "Trascina un campo su una banda; una lista crea una tabella.")}
      </p>
    </div>
  );
}

function FieldTree({
  nodes,
  depth,
  prefix = "",
}: {
  nodes: ReportTypeNode[];
  depth: number;
  /** Dotted ancestor path through OBJECT nodes — the drag payload carries the
   *  full binding key (`test.campo_3`). Resets at a collection boundary: its
   *  children are addressed RELATIVE to the child row scope (detail_report). */
  prefix?: string;
}) {
  return (
    <div>
      {nodes.map((n) => (
        <div key={n.id}>
          <div
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(
                FIELD_MIME,
                JSON.stringify({ id: `${prefix}${n.id}`, type: n.type }),
              );
              e.dataTransfer.effectAllowed = "copy";
            }}
            className="flex cursor-grab items-center gap-1.5 rounded px-1.5 py-1 text-xs hover:bg-surface"
            style={{ paddingLeft: `${depth * 14 + 6}px` }}
            title={n.label}
          >
            <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground/60" />
            {n.type === "collection" ? (
              <List className="h-3 w-3 shrink-0 text-accent" />
            ) : n.type === "object" ? (
              <Boxes className="h-3 w-3 shrink-0 text-accent" />
            ) : (
              <FileType2 className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate font-mono">{n.id}</span>
          </div>
          {n.children && (
            <FieldTree
              nodes={n.children}
              depth={depth + 1}
              prefix={n.type === "object" ? `${prefix}${n.id}.` : ""}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspector — element selector dropdown + accordion sections
// ---------------------------------------------------------------------------

function elementOptions(
  bands: ReportBand[],
  prefix: BandPath = [],
): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  bands.forEach((b, i) => {
    const path = [...prefix, i];
    const indent = "  ".repeat(path.length - 1);
    out.push({
      value: JSON.stringify({ kind: "band", path }),
      label: `${indent}${BAND_LABELS[b.kind]}${b.kind === "detail_report" && b.bind ? ` (${b.bind})` : ""}`,
    });
    b.controls.forEach((c, ci) => {
      out.push({
        value: JSON.stringify({ kind: "control", path, indices: [ci] }),
        label: `${indent}  └ ${c.kind} ${controlCaption(c)}`,
      });
    });
    if (b.bands?.length) out.push(...elementOptions(b.bands, path));
  });
  return out;
}

function Acc({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1 px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:bg-surface"
      >
        <span className={cn("inline-block transition-transform", open ? "rotate-90" : "")}>▸</span>
        {title}
      </button>
      {open && <div className="flex flex-col gap-1.5 px-3 pb-3">{children}</div>}
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  step = 1,
  disabled,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (v: number) => void;
  step?: number;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <Input
        type="number"
        step={step}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-7 text-xs"
      />
    </label>
  );
}

function SelField({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-1.5 text-xs"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <Input
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 text-xs"
      />
    </label>
  );
}

function InspectorPanel({
  layout,
  sel,
  selectedBand,
  typeNodes,
  bands,
  canWrite,
  onPatchLayout,
  onPatchBands,
  onSelect,
}: {
  layout: ReportLayout;
  sel: Selection;
  selectedBand: ReportBand | undefined;
  typeNodes: ReportTypeNode[];
  bands: ReportBand[];
  canWrite: boolean;
  onPatchLayout: (fn: (l: ReportLayout) => ReportLayout) => void;
  onPatchBands: (fn: (bands: ReportBand[]) => ReportBand[]) => void;
  onSelect: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const selValue =
    sel.kind === "page"
      ? JSON.stringify({ kind: "page" })
      : sel.kind === "band"
        ? JSON.stringify({ kind: "band", path: sel.path })
        : JSON.stringify({ kind: "control", path: sel.path, indices: [sel.indices[0] ?? 0] });

  return (
    <div>
      {/* element selector (the DevExpress "Proprietà" dropdown) */}
      <div className="border-b border-border p-2">
        <select
          value={selValue}
          aria-label={t("reports.element", "Elemento")}
          onChange={(e) => onSelect(JSON.parse(e.target.value) as Selection)}
          className="h-7 w-full rounded-md border border-border bg-background px-1.5 text-xs"
        >
          <option value={JSON.stringify({ kind: "page" })}>Report (pagina)</option>
          {elementOptions(bands).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {sel.kind === "page" && (
        <PageSetupInspector
          layout={layout}
          typeNodes={typeNodes}
          canWrite={canWrite}
          onPatchLayout={onPatchLayout}
        />
      )}
      {sel.kind === "band" && selectedBand && (
        <BandInspector
          band={selectedBand}
          path={sel.path}
          bands={bands}
          typeNodes={typeNodes}
          canWrite={canWrite}
          onPatchBands={onPatchBands}
          onSelect={onSelect}
        />
      )}
      {sel.kind === "control" && selectedBand && sel.indices.length === 1 && (
        <ControlInspector
          band={selectedBand}
          path={sel.path}
          index={sel.indices[0]}
          bands={bands}
          typeNodes={typeNodes}
          canWrite={canWrite}
          onPatchBands={onPatchBands}
          onSelect={onSelect}
        />
      )}
      {sel.kind === "control" && sel.indices.length > 1 && (
        <div className="p-3 text-xs text-muted-foreground">
          {t(
            "reports.multiSelected",
            "{{n}} controlli selezionati — usa i pulsanti Allinea/Distribuisci in toolbar, le frecce per spostarli, Canc per eliminarli.",
            { n: sel.indices.length },
          )}
        </div>
      )}
    </div>
  );
}

function PageSetupInspector({
  layout,
  typeNodes,
  canWrite,
  onPatchLayout,
}: {
  layout: ReportLayout;
  typeNodes: ReportTypeNode[];
  canWrite: boolean;
  onPatchLayout: (fn: (l: ReportLayout) => ReportLayout) => void;
}) {
  const { t } = useTranslation();
  const setup = layout.page_setup;
  const patchSetup = (p: Partial<typeof setup>) =>
    onPatchLayout((l) => ({ ...l, page_setup: { ...l.page_setup, ...p } }));

  return (
    <>
      <Acc title={t("reports.pageSetup", "Pagina")}>
        <SelField
          label={t("reports.pageKind", "Tipo")}
          value={setup.kind}
          disabled={!canWrite}
          options={[
            { value: "document", label: "Documento" },
            { value: "labels", label: "Etichette" },
          ]}
          onChange={(v) =>
            patchSetup({
              kind: v as "document" | "labels",
              labels:
                v === "labels"
                  ? (setup.labels ?? {
                      cols: 3,
                      rows: 8,
                      label_w: 63.5,
                      label_h: 33.9,
                      gap_x: 2.5,
                      gap_y: 0,
                    })
                  : setup.labels,
            })
          }
        />
        <SelField
          label={t("reports.pageFormat", "Formato")}
          value={setup.format}
          disabled={!canWrite}
          options={[
            { value: "A4", label: "A4" },
            { value: "A5", label: "A5" },
            { value: "letter", label: "Letter" },
            { value: "custom", label: "Custom" },
          ]}
          onChange={(v) => patchSetup({ format: v })}
        />
        {setup.format === "custom" && (
          <>
            <NumField
              label="L (mm)"
              value={setup.custom_w}
              disabled={!canWrite}
              onChange={(v) => patchSetup({ custom_w: v })}
            />
            <NumField
              label="A (mm)"
              value={setup.custom_h}
              disabled={!canWrite}
              onChange={(v) => patchSetup({ custom_h: v })}
            />
          </>
        )}
        <SelField
          label={t("reports.orientation", "Orient.")}
          value={setup.orientation}
          disabled={!canWrite}
          options={[
            { value: "portrait", label: "Verticale" },
            { value: "landscape", label: "Orizzontale" },
          ]}
          onChange={(v) => patchSetup({ orientation: v as "portrait" | "landscape" })}
        />
      </Acc>
      <Acc title={t("reports.margins", "Margini (mm)")} defaultOpen={false}>
        {(["top", "right", "bottom", "left"] as const).map((side) => (
          <NumField
            key={side}
            label={{ top: "Sopra", right: "Destra", bottom: "Sotto", left: "Sinistra" }[side]}
            value={setup.margins[side]}
            disabled={!canWrite}
            onChange={(v) => patchSetup({ margins: { ...setup.margins, [side]: Math.max(0, v) } })}
          />
        ))}
      </Acc>
      {setup.kind === "labels" && setup.labels && (
        <Acc title={t("reports.labelGrid", "Griglia etichette")}>
          <NumField
            label="Colonne"
            value={setup.labels.cols}
            disabled={!canWrite}
            onChange={(v) =>
              patchSetup({ labels: { ...setup.labels!, cols: Math.max(1, Math.round(v)) } })
            }
          />
          <NumField
            label="Righe"
            value={setup.labels.rows}
            disabled={!canWrite}
            onChange={(v) =>
              patchSetup({ labels: { ...setup.labels!, rows: Math.max(1, Math.round(v)) } })
            }
          />
          <NumField
            label="L cella"
            value={setup.labels.label_w}
            disabled={!canWrite}
            onChange={(v) => patchSetup({ labels: { ...setup.labels!, label_w: Math.max(1, v) } })}
          />
          <NumField
            label="A cella"
            value={setup.labels.label_h}
            disabled={!canWrite}
            onChange={(v) => patchSetup({ labels: { ...setup.labels!, label_h: Math.max(1, v) } })}
          />
          <NumField
            label="Gap X"
            value={setup.labels.gap_x ?? 0}
            disabled={!canWrite}
            onChange={(v) => patchSetup({ labels: { ...setup.labels!, gap_x: Math.max(0, v) } })}
          />
          <NumField
            label="Gap Y"
            value={setup.labels.gap_y ?? 0}
            disabled={!canWrite}
            onChange={(v) => patchSetup({ labels: { ...setup.labels!, gap_y: Math.max(0, v) } })}
          />
        </Acc>
      )}
      <Acc title={t("reports.groups", "Gruppi (dettaglio)")} defaultOpen={layout.groups.length > 0}>
        {layout.groups.map((g, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <select
              value={g.by}
              disabled={!canWrite}
              aria-label={t("reports.groupBy", "Raggruppa per")}
              onChange={(e) =>
                onPatchLayout((l) => ({
                  ...l,
                  groups: l.groups.map((gg, ii) => (ii === i ? { ...gg, by: e.target.value } : gg)),
                }))
              }
              className="h-7 flex-1 rounded-md border border-border bg-background px-1.5 text-xs"
            >
              {typeNodes.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.id}
                </option>
              ))}
            </select>
            <select
              value={g.sort ?? "asc"}
              disabled={!canWrite}
              aria-label="Ordine"
              onChange={(e) =>
                onPatchLayout((l) => ({
                  ...l,
                  groups: l.groups.map((gg, ii) =>
                    ii === i ? { ...gg, sort: e.target.value as "asc" | "desc" } : gg,
                  ),
                }))
              }
              className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
            >
              <option value="asc">↑</option>
              <option value="desc">↓</option>
            </select>
            {canWrite && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-danger"
                onClick={() =>
                  onPatchLayout((l) => ({ ...l, groups: l.groups.filter((_, ii) => ii !== i) }))
                }
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
        {canWrite && typeNodes.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() =>
              onPatchLayout((l) => ({
                ...l,
                groups: [...l.groups, { by: typeNodes[0].id, sort: "asc" }],
              }))
            }
          >
            <Plus className="mr-0.5 h-3 w-3" />
            {t("reports.addGroup", "Gruppo")}
          </Button>
        )}
      </Acc>
    </>
  );
}

function BandInspector({
  band,
  path,
  bands,
  typeNodes,
  canWrite,
  onPatchBands,
  onSelect,
}: {
  band: ReportBand;
  path: BandPath;
  bands: ReportBand[];
  typeNodes: ReportTypeNode[];
  canWrite: boolean;
  onPatchBands: (fn: (bands: ReportBand[]) => ReportBand[]) => void;
  onSelect: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const patch = (p: Partial<ReportBand>) => onPatchBands((b) => updateBandAt(b, path, p));
  const parentScope = scopeNodesFor(bands, path, typeNodes);
  const collections = flatCollections(parentScope);

  return (
    <Acc title={`${t("reports.band", "Banda")} — ${BAND_LABELS[band.kind]}`}>
      <NumField
        label={t("reports.height", "Altezza")}
        value={band.height_mm}
        disabled={!canWrite}
        onChange={(v) => patch({ height_mm: Math.max(1, v) })}
      />
      {band.kind === "detail_report" && (
        <SelField
          label="Lista"
          value={band.bind ?? ""}
          disabled={!canWrite}
          options={[
            { value: "", label: "—" },
            ...collections.map((c) => ({ value: c.id, label: c.id })),
          ]}
          onChange={(v) => patch({ bind: v || null })}
        />
      )}
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={band.auto_grow ?? true}
          disabled={!canWrite}
          onChange={(e) => patch({ auto_grow: e.target.checked })}
        />
        {t("reports.autoGrow", "Cresce col contenuto")}
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={band.keep_together ?? false}
          disabled={!canWrite}
          onChange={(e) => patch({ keep_together: e.target.checked })}
        />
        {t("reports.keepTogether", "Non spezzare su più pagine")}
      </label>
      <SelField
        label={t("reports.pageBreak", "Salto")}
        value={band.page_break ?? "none"}
        disabled={!canWrite}
        options={[
          { value: "none", label: "Nessuno" },
          { value: "before", label: "Prima" },
          { value: "after", label: "Dopo" },
        ]}
        onChange={(v) => patch({ page_break: v as "none" | "before" | "after" })}
      />
      {canWrite && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 justify-start px-2 text-xs text-danger"
          onClick={() => {
            onPatchBands((b) => removeBandAt(b, path));
            onSelect({ kind: "page" });
          }}
        >
          <Trash2 className="mr-1 h-3 w-3" />
          {t("reports.deleteBand", "Elimina banda")}
        </Button>
      )}
    </Acc>
  );
}

type PageInfoInfo = "page_number" | "page_count" | "page_x_of_y" | "print_date";

function ControlInspector({
  band,
  path,
  index,
  bands,
  typeNodes,
  canWrite,
  onPatchBands,
  onSelect,
}: {
  band: ReportBand;
  path: BandPath;
  index: number;
  bands: ReportBand[];
  typeNodes: ReportTypeNode[];
  canWrite: boolean;
  onPatchBands: (fn: (bands: ReportBand[]) => ReportBand[]) => void;
  onSelect: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const ctl = band.controls[index];
  if (!ctl) return null;
  const patch = (p: Partial<ReportControl>) =>
    onPatchBands((b) => updateControlAt(b, path, index, p));
  const patchStyle = (p: Partial<NonNullable<ReportControl["style"]>>) =>
    patch({ style: { ...(ctl.style ?? {}), ...p } });

  const scope = scopeNodesFor(bands, path, typeNodes);
  // Dotted-flattened (the `_scope_maps` grammar) so object-nested fields are
  // pickable binding targets, not just the top-level ids.
  const scopeOptions = [
    { value: "", label: "—" },
    ...flattenScope(scope).map((e) => ({ value: e.id, label: e.id })),
  ];
  const fmt = "format" in ctl ? (ctl.format ?? { kind: "text" as const }) : undefined;

  return (
    <>
      <Acc title={`${t("reports.position", "Posizione")} (mm)`}>
        <div className="grid grid-cols-2 gap-1.5">
          <NumField
            label="X"
            value={ctl.x}
            disabled={!canWrite}
            onChange={(v) => patch({ x: Math.max(0, v) })}
          />
          <NumField
            label="Y"
            value={ctl.y}
            disabled={!canWrite}
            onChange={(v) => patch({ y: Math.max(0, v) })}
          />
          <NumField
            label="L"
            value={ctl.w}
            disabled={!canWrite}
            onChange={(v) => patch({ w: Math.max(1, v) })}
          />
          <NumField
            label="A"
            value={ctl.h}
            disabled={!canWrite}
            onChange={(v) => patch({ h: Math.max(1, v) })}
          />
        </div>
      </Acc>

      <Acc title={`${t("reports.content", "Contenuto")} — ${ctl.kind}`}>
        {ctl.kind === "label" && (
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">
              {t("reports.labelText", "Testo (supporta {{ }})")}
            </span>
            <textarea
              value={ctl.text}
              disabled={!canWrite}
              onChange={(e) => patch({ text: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
            />
          </label>
        )}

        {ctl.kind === "field" && (
          <>
            <SelField
              label="Campo"
              value={ctl.bind}
              disabled={!canWrite}
              options={scopeOptions}
              onChange={(v) => patch({ bind: v })}
            />
            <SelField
              label="Formato"
              value={fmt?.kind ?? "text"}
              disabled={!canWrite}
              options={["text", "number", "currency", "date", "datetime"].map((v) => ({
                value: v,
                label: v,
              }))}
              onChange={(v) =>
                patch({ format: { ...fmt, kind: v as NonNullable<typeof fmt>["kind"] } })
              }
            />
            {(fmt?.kind === "number" || fmt?.kind === "currency") && (
              <NumField
                label="Decimali"
                value={fmt.decimals ?? 2}
                disabled={!canWrite}
                onChange={(v) =>
                  patch({ format: { ...fmt, decimals: Math.max(0, Math.round(v)) } })
                }
              />
            )}
            {fmt?.kind === "currency" && (
              <TextField
                label="Simbolo"
                value={fmt.symbol ?? "€"}
                disabled={!canWrite}
                onChange={(v) => patch({ format: { ...fmt, symbol: v } })}
              />
            )}
            {(fmt?.kind === "date" || fmt?.kind === "datetime") && (
              <TextField
                label="Pattern"
                value={fmt.pattern ?? ""}
                placeholder="%d/%m/%Y"
                disabled={!canWrite}
                onChange={(v) => patch({ format: { ...fmt, pattern: v || null } })}
              />
            )}
            {AGG_BANDS.has(band.kind) && (
              <SelField
                label="Aggreg."
                value={ctl.agg ?? ""}
                disabled={!canWrite}
                options={[
                  { value: "", label: "—" },
                  ...AGG_OPS.map((a) => ({ value: a, label: a })),
                ]}
                onChange={(v) => patch({ agg: (v || null) as FieldControl["agg"] })}
              />
            )}
          </>
        )}

        {ctl.kind === "image" && (
          <>
            <TextField
              label="URL"
              value={ctl.src ?? ""}
              disabled={!canWrite}
              onChange={(v) => patch({ src: v || null })}
            />
            <SelField
              label="Campo"
              value={ctl.bind ?? ""}
              disabled={!canWrite}
              options={scopeOptions}
              onChange={(v) => patch({ bind: v || null })}
            />
            <SelField
              label="Fit"
              value={ctl.fit ?? "contain"}
              disabled={!canWrite}
              options={["contain", "cover", "fill"].map((v) => ({ value: v, label: v }))}
              onChange={(v) => patch({ fit: v as "contain" | "cover" | "fill" })}
            />
          </>
        )}

        {ctl.kind === "line" && (
          <SelField
            label="Direzione"
            value={ctl.direction ?? "horizontal"}
            disabled={!canWrite}
            options={[
              { value: "horizontal", label: "Orizzontale" },
              { value: "vertical", label: "Verticale" },
            ]}
            onChange={(v) => patch({ direction: v as "horizontal" | "vertical" })}
          />
        )}

        {ctl.kind === "shape" && (
          <NumField
            label="Raggio"
            value={ctl.radius ?? 0}
            disabled={!canWrite}
            onChange={(v) => patch({ radius: Math.max(0, v) })}
          />
        )}

        {ctl.kind === "page_info" && (
          <SelField
            label="Info"
            value={ctl.info ?? "page_number"}
            disabled={!canWrite}
            options={[
              { value: "page_number", label: "N. pagina" },
              { value: "page_count", label: "Tot. pagine" },
              { value: "page_x_of_y", label: "Pagina X di Y" },
              { value: "print_date", label: "Data di stampa" },
            ]}
            onChange={(v) => patch({ info: v as PageInfoInfo })}
          />
        )}

        {ctl.kind === "checkbox" && (
          <>
            <SelField
              label="Campo"
              value={ctl.bind}
              disabled={!canWrite}
              options={scopeOptions}
              onChange={(v) => patch({ bind: v })}
            />
            <TextField
              label="Etichetta"
              value={ctl.text ?? ""}
              disabled={!canWrite}
              onChange={(v) => patch({ text: v })}
            />
          </>
        )}

        {ctl.kind === "rich_text" && (
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">
              {t("reports.richText", "Testo (**grassetto**, *corsivo*, a-capo)")}
            </span>
            <textarea
              value={ctl.text}
              disabled={!canWrite}
              onChange={(e) => patch({ text: e.target.value })}
              rows={6}
              className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
            />
          </label>
        )}

        {ctl.kind === "barcode" && (
          <>
            <SelField
              label="Tipo"
              value={ctl.symbology ?? "qr"}
              disabled={!canWrite}
              options={[
                { value: "qr", label: "QR" },
                { value: "code128", label: "Code 128" },
                { value: "ean13", label: "EAN-13" },
              ]}
              onChange={(v) => patch({ symbology: v as BarcodeControl["symbology"] })}
            />
            <SelField
              label="Campo"
              value={ctl.bind ?? ""}
              disabled={!canWrite}
              options={scopeOptions}
              onChange={(v) => patch({ bind: v || null })}
            />
            <TextField
              label="Valore fisso"
              value={ctl.value ?? ""}
              placeholder={t("reports.barcodeValueHint", "usato se nessun campo")}
              disabled={!canWrite}
              onChange={(v) => patch({ value: v || null })}
            />
            {(ctl.symbology ?? "qr") !== "qr" && (
              <label className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={ctl.show_text ?? true}
                  disabled={!canWrite}
                  onChange={(e) => patch({ show_text: e.target.checked })}
                />
                {t("reports.barcodeShowText", "Testo sotto le barre")}
              </label>
            )}
          </>
        )}

        {ctl.kind === "table" && (
          <TableColumnsEditor
            ctl={ctl}
            scope={scope}
            canWrite={canWrite}
            onPatch={(p) => patch(p)}
          />
        )}

        <TextField
          label="Visibile se"
          value={ctl.visible_when ?? ""}
          placeholder="$json.urgente"
          disabled={!canWrite}
          onChange={(v) => patch({ visible_when: v || null })}
        />
      </Acc>

      <Acc title={t("reports.style", "Stile")} defaultOpen={false}>
        <NumField
          label="Font pt"
          value={ctl.style?.font_size}
          disabled={!canWrite}
          onChange={(v) => patchStyle({ font_size: v > 0 ? v : null })}
        />
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={ctl.style?.bold ?? false}
              disabled={!canWrite}
              onChange={(e) => patchStyle({ bold: e.target.checked })}
            />
            <b>B</b>
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={ctl.style?.italic ?? false}
              disabled={!canWrite}
              onChange={(e) => patchStyle({ italic: e.target.checked })}
            />
            <i>I</i>
          </label>
          <select
            value={ctl.style?.align ?? ""}
            disabled={!canWrite}
            aria-label={t("reports.align", "Allineamento")}
            onChange={(e) =>
              patchStyle({
                align: (e.target.value || null) as NonNullable<ReportControl["style"]>["align"],
              })
            }
            className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
          >
            <option value="">auto</option>
            <option value="left">sx</option>
            <option value="center">centro</option>
            <option value="right">dx</option>
          </select>
        </div>
        <TextField
          label="Colore"
          value={ctl.style?.color ?? ""}
          placeholder="#111111"
          disabled={!canWrite}
          onChange={(v) => patchStyle({ color: v || null })}
        />
        <TextField
          label="Sfondo"
          value={ctl.style?.background ?? ""}
          placeholder="#f5f5f5"
          disabled={!canWrite}
          onChange={(v) => patchStyle({ background: v || null })}
        />
        <NumField
          label="Bordo mm"
          value={ctl.style?.border_width}
          step={0.1}
          disabled={!canWrite}
          onChange={(v) => patchStyle({ border_width: v > 0 ? v : null })}
        />
        <TextField
          label="Col.bordo"
          value={ctl.style?.border_color ?? ""}
          placeholder="#000000"
          disabled={!canWrite}
          onChange={(v) => patchStyle({ border_color: v || null })}
        />
        <NumField
          label="Padding"
          value={ctl.style?.padding}
          step={0.5}
          disabled={!canWrite}
          onChange={(v) => patchStyle({ padding: v > 0 ? v : null })}
        />
      </Acc>

      {canWrite && (
        <div className="p-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-danger"
            onClick={() => {
              onPatchBands((b) =>
                updateBandAt(b, path, (bb) => ({
                  ...bb,
                  controls: bb.controls.filter((_, i) => i !== index),
                })),
              );
              onSelect({ kind: "band", path });
            }}
          >
            <Trash2 className="mr-1 h-3 w-3" />
            {t("reports.deleteControl", "Elimina controllo")}
          </Button>
        </div>
      )}
    </>
  );
}

function TableColumnsEditor({
  ctl,
  scope,
  canWrite,
  onPatch,
}: {
  ctl: TableControl;
  scope: ReportTypeNode[];
  canWrite: boolean;
  onPatch: (p: Partial<TableControl>) => void;
}) {
  const { t } = useTranslation();
  const collections = flatCollections(scope);
  const coll = collections.find((e) => e.id === ctl.bind)?.node;
  const childOptions = flattenScope(coll?.children ?? [])
    .filter((e) => e.node.type !== "object" && e.node.type !== "collection")
    .map((e) => ({ value: e.id, label: e.id }));

  return (
    <div className="flex flex-col gap-1.5">
      <SelField
        label="Lista"
        value={ctl.bind}
        disabled={!canWrite}
        options={[
          { value: "", label: "—" },
          ...collections.map((c) => ({ value: c.id, label: c.id })),
        ]}
        onChange={(v) => onPatch({ bind: v })}
      />
      <div className="text-[11px] text-muted-foreground">{t("reports.columns", "Colonne")}</div>
      {ctl.columns.map((col, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input
            value={col.header ?? ""}
            placeholder="Intestazione"
            disabled={!canWrite}
            onChange={(e) =>
              onPatch({
                columns: ctl.columns.map((c, ii) =>
                  ii === i ? { ...c, header: e.target.value } : c,
                ),
              })
            }
            className="h-7 flex-1 text-xs"
          />
          <select
            value={col.bind}
            disabled={!canWrite}
            aria-label="Campo colonna"
            onChange={(e) =>
              onPatch({
                columns: ctl.columns.map((c, ii) =>
                  ii === i ? { ...c, bind: e.target.value } : c,
                ),
              })
            }
            className="h-7 w-24 rounded-md border border-border bg-background px-1 text-xs"
          >
            <option value="">—</option>
            {childOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {canWrite && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-1 text-danger"
              onClick={() => onPatch({ columns: ctl.columns.filter((_, ii) => ii !== i) })}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}
      {canWrite && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() =>
            onPatch({
              columns: [...ctl.columns, { header: "", bind: childOptions[0]?.value ?? "" }],
            })
          }
        >
          <Plus className="mr-0.5 h-3 w-3" />
          {t("reports.addColumn", "Colonna")}
        </Button>
      )}
    </div>
  );
}
